import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  IconButton,
  InputLabel,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Datetime from 'react-datetime';
import firebase from 'config/config';
import CustomInput from 'components/CustomInput/CustomInput.jsx';
import swal from 'sweetalert';
import moment from 'moment';
import Popup from 'reactjs-popup';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Dropzone from 'react-dropzone';
import apiCaller from '../../../../utils/apiCallerOutstanding';
import logger from '../../../../utils/logger';
import { mappedCurrentActiveRole } from '../../../../utils/roleMapping';
import { fileUploaderNew } from '../../../../utils/fileUploadHandler';
import getFileMimeType from '../../../../utils/getFileMimeType';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: '10px',
    overflow: 'visible',
  },
  rajdhaniFont: {
    fontFamily: 'Rajdhani',
    margin: 0,
  },
  header: { textAlign: 'left', padding: '10px 16px' },
  leftAlign: { textAlign: 'left' },
  submitButton: {
    background: '#3366FF',
    color: 'white',
    fontSize: '12px',
    textTransform: 'capitalize',
  },
  text: {
    margin: '0px',
    marginTop: '5px',
    fontWeight: '400',
    fontSize: '16px',
  },
  flexbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  flexSpace: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  viewButton: {
    background: '#52BE56',
    color: 'white',
    padding: '5px 15px 5px 15px',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    border: '2px dashed lightgray',
    borderRadius: '5px',
  },
  deleteModalButton: {
    width: '45%',
    background: '#52BE56',
    borderRadius: 0,
    color: 'white',
    margin: '5px',
  },
  cancelButton: {
    width: '45%',
    background: 'lightgray',
    borderRadius: 0,
    margin: '5px',
  },
  deleteButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  deleteModal: {
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const contentStyle = { width: '24%' };

const InvoiceDetails = ({ currentTransactionData, activeRole }) => {
  const { transactionId } = currentTransactionData;
  const [isLoading, setIsLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewFileLoading, setViewFileLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceNumber: '',
    invoiceAmount: '',
    invoiceDate: null,
    submittedDate: null,
    invoiceFile: false,
  });
  const [invoiceData, setInvoiceData] = useState({});
  const classes = useStyles();

  const addInvoiceAccess =
    activeRole === 'branch-ops' ||
    activeRole === 'finance' ||
    activeRole === 'finance-recievable';

  const uploadInvoiceAccess =
    activeRole === 'finance' || activeRole === 'finance-recievable';

  const currentActiveRole = mappedCurrentActiveRole(activeRole);

  const handleChange = e => {
    const { id, value } = e.target;
    setInvoiceDetails(prevState => ({
      ...prevState,
      [id]: value,
    }));
  };

  const handleDateChange = (date, name) => {
    setInvoiceDetails(prevState => ({
      ...prevState,
      [name]: date,
    }));
  };

  const isFormValid =
    invoiceDetails.invoiceNumber &&
    invoiceDetails.invoiceAmount &&
    invoiceDetails.invoiceDate &&
    invoiceDetails.submittedDate;

  const isDataAvailable =
    invoiceData?.invoice_number ||
    invoiceData?.invoice_amount ||
    invoiceData?.invoice_date ||
    invoiceData?.invoice_submitted_date;

  const detailsChanged =
    invoiceDetails.invoiceNumber === (invoiceData?.invoice_number || '') &&
    invoiceDetails.invoiceAmount === (invoiceData?.invoice_amount || '') &&
    (invoiceData?.invoice_date
      ? moment(invoiceDetails.invoiceDate).isSame(
          moment(invoiceData?.invoice_date),
          'day'
        )
      : invoiceDetails.invoiceDate === null) &&
    (invoiceData?.invoice_submitted_date
      ? moment(invoiceDetails.submittedDate).isSame(
          moment(invoiceData?.invoice_submitted_date),
          'day'
        )
      : invoiceDetails.submittedDate === null);

  const handleGetInvoiceDetails = async () => {
    setIsLoading(true);
    const userDetails = firebase.auth().currentUser;
    if (userDetails) {
      const { Aa: token } = userDetails;
      try {
        const endPoint = `pipe/view-invoice?transaction_id=${transactionId}&role=${currentActiveRole}`;
        const response = await apiCaller(
          endPoint,
          {},
          `Bearer ${token}`,
          'nh-base',
          'get'
        );
        const data = response?.data;
        setInvoiceData(data);
        setInvoiceDetails({
          invoiceNumber: data?.invoice_number || '',
          invoiceAmount: data?.invoice_amount || '',
          invoiceDate: data?.invoice_date ? moment(data?.invoice_date) : null,
          submittedDate: data?.invoice_submitted_date
            ? moment(data?.invoice_submitted_date)
            : null,
          invoiceFile: data?.invoice_file_exists || false,
        });
        setIsLoading(false);
      } catch (e) {
        logger.error(e);
        setIsLoading(false);
      }
    }
  };

  const updateInvoiceDetails = async () => {
    setUpdating(true);
    const userDetails = firebase.auth().currentUser;

    if (userDetails) {
      const { Aa: token } = userDetails;
      try {
        const endPoint = `pipe/create-invoice`;
        const body = {
          role: currentActiveRole,
          transaction_id: transactionId,
          invoice_number: invoiceDetails.invoiceNumber,
          invoice_amount: invoiceDetails.invoiceAmount,
          invoice_date: moment(invoiceDetails.invoiceDate).format('YYYY-MM-DD'),
          invoice_submitted_date: moment(invoiceDetails.submittedDate).format(
            'YYYY-MM-DD'
          ),
        };
        await apiCaller(endPoint, body, `Bearer ${token}`, 'nh-base', 'post');
        setUpdating(false);
        swal('Success', `Invoice details updated successfully`, 'success');
        handleGetInvoiceDetails();
      } catch (e) {
        logger.error(e);
        setUpdating(false);
        swal(
          'Error',
          `Failed to update invoice details, Please try again!`,
          'error'
        );
      }
    }
  };

  const deleteInvoiceDetails = async () => {
    setIsLoading(true);
    const userDetails = firebase.auth().currentUser;

    if (userDetails) {
      const { Aa: token } = userDetails;
      const body = {
        transaction_id: transactionId,
        role: currentActiveRole,
      };
      try {
        const endPoint = `pipe/void-invoice`;
        await apiCaller(endPoint, body, `Bearer ${token}`, 'nh-base', 'post');
        swal('Success', `Deleted the invoice details successfully`, 'success');
        handleGetInvoiceDetails();
      } catch (e) {
        logger.error(e);
        setIsLoading(false);
        swal(
          'Error',
          `Failed to delete invoice details, Please try again!`,
          'error'
        );
      }
    }
  };

  const viewFile = fileUrl => {
    if (!fileUrl) return;
    window.open(fileUrl, '_blank');
  };

  const getFileUrl = async () => {
    setViewFileLoading(true);
    const userDetails = firebase.auth().currentUser;
    if (userDetails) {
      const { Aa: token } = userDetails;
      try {
        const endPoint = `transactions/v1/invoice-url?transaction_id=${transactionId}&role=${currentActiveRole}`;
        const response = await apiCaller(
          endPoint,
          {},
          `Bearer ${token}`,
          'nh-base',
          'get'
        );
        const data = response?.data;
        if (data?.url) {
          viewFile(data.url);
        } else {
          swal('Error', `Invoice file not found`, 'error');
        }
      } catch (e) {
        logger.error(e);
        swal(
          'Error',
          `Failed to fetch invoice file, Please try again!`,
          'error'
        );
      } finally {
        setViewFileLoading(false);
      }
    }
  };

  const deleteFile = async () => {
    setDeleteLoading(true);
    const userDetails = firebase.auth().currentUser;
    if (userDetails) {
      const { Aa: token } = userDetails;
      const body = {
        transaction_id: transactionId,
        role: currentActiveRole,
      };
      try {
        const endPoint = `transactions/v1/delete-invoice`;
        await apiCaller(endPoint, body, `Bearer ${token}`, 'nh-base', 'post');
        await new Promise(resolve => setTimeout(resolve, 2000));
        await handleGetInvoiceDetails();
        swal('Success', `Deleted the invoice file successfully`, 'success');
      } catch (e) {
        logger.error(e);
        swal(
          'Error',
          `Failed to delete invoice file, Please try again!`,
          'error'
        );
      } finally {
        setDeleteLoading(false);
      }
    }
  };
  const fetchS3URl = async UploadedFile => {
    setUploading(true);
    const mime = await getFileMimeType(UploadedFile);
    if (mime !== 'Unknown file type') {
      const body = {
        transaction_id: transactionId,
        role: currentActiveRole,
        extension: mime,
      };
      const endPoint = `transactions/v1/invoice`;
      const userDetails = firebase.auth().currentUser;
      if (userDetails) {
        const { Aa: token } = userDetails;
        try {
          const response = await apiCaller(
            endPoint,
            body,
            `Bearer ${token}`,
            'nh-base',
            'post'
          );
          const { url: signedURL, metadata } = response?.data;
          if (signedURL) {
            await fileUploaderNew(signedURL, metadata, UploadedFile);
            await new Promise(resolve => setTimeout(resolve, 2000));
            await handleGetInvoiceDetails();
          } else {
            swal('Error', `Failed to get signed URL`, 'error');
          }
        } catch (e) {
          logger.error(e);
          swal(
            'Error',
            `Failed to fetch signed URL, Please try again!`,
            'error'
          );
        } finally {
          setUploading(false);
        }
      }
    } else {
      setUploading(false);
      swal('Oops!', 'Unknown file type. Please upload a valid file.', 'error');
    }
  };

  useEffect(() => {
    handleGetInvoiceDetails();
  }, [transactionId]);

  return (
    <Card className={classes.root}>
      <CardHeader
        title={
          <div className={classes.flexSpace}>
            <div className={classes.flexbox}>
              <p className={classes.rajdhaniFont}>Invoice Details</p>
              {activeRole === 'ops' && isDataAvailable && (
                <Popup
                  trigger={
                    <IconButton color="primary" component="span" size="small">
                      <DeleteOutlineIcon color="error" />
                    </IconButton>
                  }
                  modal
                  {...{
                    contentStyle,
                  }}
                >
                  {close => (
                    <div className={classes.deleteModal}>
                      <HighlightOffIcon fontSize="large" color="error" />
                      <h4 style={{ fontWeight: 'bold' }}>Are You Sure?</h4>
                      <p>If you proceed, you will not be able to recover it</p>
                      <div className={classes.deleteButtonContainer}>
                        <Button
                          onClick={close}
                          className={classes.cancelButton}
                        >
                          No
                        </Button>
                        <Button
                          onClick={deleteInvoiceDetails}
                          className={classes.deleteModalButton}
                          disabled={isLoading}
                        >
                          Yes
                        </Button>
                      </div>
                    </div>
                  )}
                </Popup>
              )}
            </div>
            <div className={classes.flexbox}>
              {invoiceDetails.invoiceFile && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => getFileUrl()}
                  className={classes.viewButton}
                  disabled={viewFileLoading || deleteLoading}
                >
                  View
                </Button>
              )}
              {invoiceDetails.invoiceFile && activeRole === 'ops' && (
                <Popup
                  trigger={
                    <IconButton
                      color="primary"
                      size="small"
                      disabled={deleteLoading}
                    >
                      <DeleteOutlineIcon color="error" />
                    </IconButton>
                  }
                  modal
                  {...{ contentStyle }}
                >
                  {close => (
                    <div className={classes.deleteModal}>
                      <HighlightOffIcon fontSize="large" color="error" />
                      <h4 style={{ fontWeight: 'bold' }}>Are You Sure?</h4>
                      <p>If you proceed, you will not be able to recover it</p>
                      <div className={classes.deleteButtonContainer}>
                        <Button
                          onClick={close}
                          className={classes.cancelButton}
                        >
                          No
                        </Button>
                        <Button
                          onClick={() => {
                            deleteFile();
                            close();
                          }}
                          className={classes.deleteModalButton}
                          disabled={deleteLoading}
                        >
                          Yes
                        </Button>
                      </div>
                    </div>
                  )}
                </Popup>
              )}
            </div>
          </div>
        }
        component={Typography}
        className={classes.header}
      />
      <CardContent>
        {isLoading ? (
          <CircularProgress size={15} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              {addInvoiceAccess ? (
                <CustomInput
                  labelText="Invoice Number"
                  id="invoiceNumber"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    value: invoiceDetails.invoiceNumber,
                    onChange: handleChange,
                  }}
                />
              ) : (
                <div className={classes.leftAlign}>
                  <InputLabel>Invoice Number</InputLabel>
                  <h5 className={classes.text}>
                    {invoiceDetails.invoiceNumber || '–'}
                  </h5>
                </div>
              )}
            </Grid>
            <Grid item xs={6}>
              {addInvoiceAccess ? (
                <CustomInput
                  style={{ paddingTop: '10px' }}
                  labelText="Invoice Amount"
                  id="invoiceAmount"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{
                    type: 'number',
                    value: invoiceDetails.invoiceAmount,
                    onChange: handleChange,
                    inputProps: {
                      min: 0,
                      onKeyDown: e => {
                        if (e.key === '-' || e.key === '+') {
                          e.preventDefault();
                        }
                      },
                    },
                    style: { fontSize: '18px', color: 'black' },
                  }}
                />
              ) : (
                <div className={classes.leftAlign}>
                  <InputLabel>Invoice Amount</InputLabel>
                  <h5 className={classes.text}>
                    {invoiceDetails.invoiceAmount
                      ? `₹ ${invoiceDetails.invoiceAmount}`
                      : '–'}
                  </h5>
                </div>
              )}
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                className={classes.leftAlign}
                style={{ color: '#777575' }}
              >
                Invoice Date
              </Typography>
              {addInvoiceAccess ? (
                <Datetime
                  value={invoiceDetails.invoiceDate}
                  dateFormat="DD-MM-YYYY"
                  timeFormat={false}
                  onChange={date => handleDateChange(date, 'invoiceDate')}
                  closeOnSelect
                />
              ) : (
                <h5 className={`${classes.text} ${classes.leftAlign}`}>
                  {invoiceDetails.invoiceDate
                    ? moment(invoiceDetails.invoiceDate).format('DD-MM-YYYY')
                    : '–'}
                </h5>
              )}
            </Grid>

            <Grid item xs={6}>
              <Typography
                variant="body2"
                className={classes.leftAlign}
                style={{ color: '#777575' }}
              >
                Submitted Date
              </Typography>
              {addInvoiceAccess ? (
                <Datetime
                  value={invoiceDetails.submittedDate}
                  dateFormat="DD-MM-YYYY"
                  timeFormat={false}
                  onChange={date => handleDateChange(date, 'submittedDate')}
                  closeOnSelect
                />
              ) : (
                <h5 className={`${classes.text} ${classes.leftAlign}`}>
                  {invoiceDetails.submittedDate
                    ? moment(invoiceDetails.submittedDate).format('DD-MM-YYYY')
                    : '–'}
                </h5>
              )}
            </Grid>
            {uploadInvoiceAccess && !invoiceDetails.invoiceFile && (
              <Grid item xs={12}>
                <Dropzone
                  accept="image/png,image/jpeg,application/pdf"
                  maxFiles={1}
                  onDrop={acceptedFiles => {
                    if (!acceptedFiles.length) return;
                    fetchS3URl(acceptedFiles[0]);
                  }}
                >
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className={classes.container}>
                      <input {...getInputProps()} />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexDirection: 'column',
                          marginTop: 5,
                        }}
                      >
                        {uploading ? (
                          <CircularProgress />
                        ) : (
                          <>
                            <CloudUploadIcon />
                            <p
                              style={{
                                fontSize: '10px',
                                color: 'gray',
                              }}
                            >
                              Drag and Drop or Click here to upload
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </Dropzone>
              </Grid>
            )}
          </Grid>
        )}
        {addInvoiceAccess && (
          <div
            style={{ display: 'flex', justifyContent: 'end', marginTop: '5px' }}
          >
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.submitButton}
              disabled={!isFormValid || updating || detailsChanged}
              onClick={updateInvoiceDetails}
            >
              Save {updating && <CircularProgress size={5} />}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default memo(InvoiceDetails);
